/**
 * Flag declaration schema — declared ids plus initial values (booleans in v0).
 *
 * @remarks
 * Flags are the world's boolean memory: lock states, story beats, one-shot
 * gates. Every flag a pack references (in conditions, effects, or exit locks)
 * must be declared here; `content-loader` cross-checks that in R3.
 *
 * **Initial values live HERE, not in the manifest.** The plan's §3 manifest
 * sketch listed "initial flags/inventory" while also giving the flags section
 * "declared ids + initial values" — one fact, two homes. R1 resolves the
 * conflict DRY-wise: a flag's starting value is part of its declaration, and
 * the manifest carries only `initialInventory`. Recorded in the R1 handoff.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { flagIdSchema } from './identifiers.js';
import { type FactScope, factScopeSchema, nonBlankStringSchema } from './primitives.js';

/**
 * Whose fact a flag records (D146, Mike's ruling: some state is the
 * player's, not the world's) — {@link factScopeSchema} worn by a flag.
 *
 * @remarks
 * A `world` flag is a fact about the facility — a door thrown, a clock
 * running — and the checkpoint puts it back with the rest of the world
 * (D142). A `player` flag is a fact the player has LEARNED — the frame on the
 * film, once seen — and it survives the checkpoint the way the hands do: you
 * cannot un-know the frame any more than you can un-carry the film. Default
 * `world`, because most flags are.
 */
export const flagScopeSchema = factScopeSchema;

/** A flag's scope — see {@link flagScopeSchema}. */
export type FlagScope = FactScope;

/** Declares one flag: its id, its starting value, whose fact it is, and an optional authoring note. */
export const flagDefinitionSchema = z
	.strictObject({
		description: nonBlankStringSchema.optional(),
		flagId: flagIdSchema,
		initialValue: z.boolean(),
		scope: flagScopeSchema.default('world'),
	})
	.readonly();

/** One declared flag — see {@link flagDefinitionSchema}. */
export type FlagDefinition = z.infer<typeof flagDefinitionSchema>;
